import { Box, Button, IconButton, Select, TextField, Typography, MenuItem, Tooltip } from '@mui/material';
import { grey } from '@mui/material/colors';
import {useTheme } from '@mui/system';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import arrow from '../assets/icons/canvasIcons/arrowTwo.svg';
import CloudUploadIcon from '../assets/icons/canvasIcons/cloud_upload.svg';
import Deleteicon from '../assets/icons/canvasIcons/deleteicon.svg';
import Downloadicon from '../assets/icons/canvasIcons/downlaodicon.svg';
import LoadingCanva from '../components/LoadingCanva.tsx';
import UndoRedoControls from './UndoRedoControls.tsx';
import CycleIcon from '../icons/CycleIcon.tsx';
import RefreshIcon from '../icons/RefreshIcon.tsx';
import { useCanvaSettings } from '../context/useCanvaSettings';
import { GenerateImageResponse } from '../types/CanvaGenerateInterface';
import CanvasActionBar from '../components/common/CanvasActionBar';
import VideoTopActionBar from '../components/common/VideoTopActionBar';
import { SaagasImage } from '../types/SaagasImage';
import { useModelParameters } from '../hooks/useModelParameters';
import { useImageModels } from '../hooks/useImageModels';
import { useLocation } from 'react-router-dom';
import VideoPlayIcon from '../icons/VideoPlayIcon';
import VideoPauseIcon from '../icons/VideoPauseIcon';
import { PlayArrow, Pause, Fullscreen, FullscreenExit, VolumeUp, VolumeOff } from '@mui/icons-material';
import { useSnackbar } from '../components/context/SnackbarContext';

interface CanvasWorkspaceProps {
  uploadedImage: string | null;
  isGenerating: boolean;
  isOutpaintActive: boolean;
  hasOutpaintAspect: boolean;
  isOutpaintFrameLocked: boolean;
  canvasDimensions: { width: number; height: number };
  imageBox: { x: number; y: number; width: number; height: number };
  editImagePrompt: string;
  isDownloading: boolean;
  locationState?: {
    width?: number;
    height?: number;
    fromEditIcon?: boolean;
    id?: string;
    timestamp?: number;
    style?: unknown;
    influencer?: unknown;
  };
  getRootProps: () => any;
  getInputProps: () => any;
  handleMouseDown: (e: React.MouseEvent, type: 'resize' | 'drag', handle?: string) => void;
  setEditImagePrompt: (value: string) => void;
  replaceUsingPrompt: () => void;
  download: () => void;
  handleDeleteImage: () => void;
  handleRedo?: () => void;
  handleUndo?: () => void;
  canvaImageBoolean?:boolean;
  generatedImages?: Array<GenerateImageResponse>;
  onImageSelect?: (imageUrl: string, imageId: string) => void;
  currentImageId?: string | null;
  viewMode?: 'grid' | 'list';
  contentTypeTab?: 'image' | 'video';
}

// Model categories (kept for backward compatibility with old models)
const EDIT_MODELS = ['FLUX_Kontext_Edit', 'Canvas_AI_Editor'];
const EDIT_MODEL_DISPLAY_NAME = 'Google - Nano Banana Pro';
const EDIT_MODEL_TYPE = 'image-editing';
const EDIT_MODEL_ID = '6929dd38654c3aff1422bde1';

// Helper function to determine if a model is for editing (legacy support)
const isEditModel = (model: string): boolean => {
  return EDIT_MODELS.some(editModel => model.includes(editModel) || model.toLowerCase().includes('edit'));
};

// Helper function to get model category based on modelType from API
const getModelCategoryFromType = (modelType: string | null | undefined): 'Generate' | 'Edit' => {
  if (modelType === 'image-editing') {
    return 'Edit';
  }
  if (modelType === 'text-to-image') {
    return 'Generate';
  }
  // Fallback to legacy check if modelType is not available
  return 'Generate';
};

// Helper function to check if a URL is a video
const isVideoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.mp4') || 
         url.toLowerCase().endsWith('.webm') || 
         url.toLowerCase().endsWith('.mov') ||
         url.toLowerCase().includes('/videos/');
};

// VideoPlayer component with custom play/pause controls and control bar
interface VideoPlayerProps {
  src: string;
  sx?: any;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ src, sx }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const volume = 1; // Volume is constant for now (only mute/unmute is implemented)
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Format time in MM:SS format
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleProgress = () => {
      if (!video.buffered.length || !video.duration) return;
      // Find the buffered range that contains the current time
      let bufferedEnd = 0;
      for (let i = 0; i < video.buffered.length; i++) {
        if (video.buffered.start(i) <= video.currentTime && video.currentTime < video.buffered.end(i)) {
          bufferedEnd = video.buffered.end(i);
          break;
        }
      }
      // If no buffer found for current time, find the furthest buffered point
      if (bufferedEnd === 0 && video.buffered.length > 0) {
        for (let i = 0; i < video.buffered.length; i++) {
          if (video.buffered.end(i) > bufferedEnd) {
            bufferedEnd = video.buffered.end(i);
          }
        }
      }
      setBuffered(bufferedEnd);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      // Update buffer on metadata load
      handleProgress();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // Hide controls after 3 seconds when playing
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      // Show controls when paused
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      setShowControls(true);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
      setCurrentTime(0);
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isDragging]);

  // Sync volume and muted state with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = volume;
    video.muted = isMuted;
  }, [isMuted]); // volume is constant, so not in dependencies

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on controls
    if ((e.target as HTMLElement).closest('.video-controls-bar')) {
      return;
    }
    e.stopPropagation();
    togglePlayPause();
  };

  const handleMouseEnter = () => {
    setShowControls(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      // Hide controls after 3 seconds when playing
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    const progressBar = progressBarRef.current;
    if (!video || !progressBar || !duration) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleProgressBarMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleProgressBarClick(e);
  };

  const handleProgressBarMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      handleProgressBarClick(e);
    }
  };

  const handleProgressBarMouseUp = () => {
    setIsDragging(false);
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen().catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPercentage = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <Box
      ref={containerRef}
      sx={{
        ...sx,
        position: 'relative',
        cursor: 'pointer',
        backgroundColor: '#000',
        borderRadius: isFullscreen ? 0 : '8px',
        overflow: 'hidden',
        width: isFullscreen ? '100vw' : undefined,
        height: isFullscreen ? '100vh' : undefined,
        display: isFullscreen ? 'flex' : 'block',
        alignItems: isFullscreen ? 'center' : undefined,
        justifyContent: isFullscreen ? 'center' : undefined,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        component="video"
        ref={videoRef}
        src={src}
        loop
        sx={{
          width: isFullscreen ? '100vw' : 'auto',
          height: isFullscreen ? '100vh' : 'auto',
          maxWidth: isFullscreen ? '100%' : '100%',
          maxHeight: isFullscreen ? '100%' : '500px',
          objectFit: 'contain',
          display: 'block',
        }}
        onClick={handleVideoClick}
      />
      
      {/* Center Play/Pause Overlay */}
      <Box
        onClick={handleVideoClick}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          cursor: 'pointer',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s ease',
          zIndex: 50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: showControls ? 'auto' : 'none',
        }}
      >
        {isPlaying ? (
          <VideoPauseIcon style={{ width: '72px', height: '72px' }} />
        ) : (
          <VideoPlayIcon style={{ width: '72px', height: '72px' }} />
        )}
      </Box>
      
      {/* Overlay Control Bar - Transparent background overlaid on video */}
      <Box
        className="video-controls-bar"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.2s ease',
          zIndex: 100,
          pointerEvents: showControls ? 'auto' : 'none',
          borderRadius: isFullscreen ? 0 : '0 0 8px 8px',
        }}
      >
        {/* Progress Bar - Full width above controls */}
        <Box
          ref={progressBarRef}
          onClick={handleProgressBarClick}
          onMouseDown={handleProgressBarMouseDown}
          onMouseMove={handleProgressBarMouseMove}
          onMouseUp={handleProgressBarMouseUp}
          onMouseLeave={handleProgressBarMouseUp}
          sx={{
            width: '100%',
            height: '6px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            position: 'relative',
            cursor: 'pointer',
            '&:hover': {
              height: '8px',
              '& .scrubber': {
                opacity: 1,
              },
            },
            '&:hover .scrubber': {
              opacity: 1,
            },
          }}
        >
          {/* Buffer Progress - Shows loaded/buffered portion */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${bufferedPercentage}%`,
              backgroundColor: 'rgba(28, 176, 200, 0.3)',
              transition: 'width 0.1s linear',
            }}
          />
          {/* Progress Fill - Shows played portion */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${progressPercentage}%`,
              backgroundColor: '#1CB0C8',
              transition: isDragging ? 'none' : 'width 0.1s linear',
              zIndex: 1,
            }}
          />
          {/* Scrubber */}
          <Box
            className="scrubber"
            sx={{
              position: 'absolute',
              left: `${progressPercentage}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '14px',
              height: '14px',
              backgroundColor: '#1CB0C8',
              borderRadius: '50%',
              opacity: isDragging ? 1 : 0,
              transition: 'opacity 0.2s ease',
              cursor: 'pointer',
              boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.8)',
              zIndex: 2,
            }}
          />
        </Box>

        {/* Control Buttons Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 18px',
            gap: '12px',
            minHeight: '78px',
          }}
        >
          {/* Play/Pause Button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            sx={{
              color: '#fff',
              padding: 0,
              width: '54px',
              height: '54px',
              minWidth: '54px',
              minHeight: '54px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiSvgIcon-root': {
                margin: 0,
                fontSize: '36px',
              },
            }}
          >
            {isPlaying ? (
              <Pause sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            ) : (
              <PlayArrow sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            )}
          </IconButton>

          {/* Volume Button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}
            sx={{
              color: '#fff',
              padding: 0,
              width: '54px',
              height: '54px',
              minWidth: '54px',
              minHeight: '54px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiSvgIcon-root': {
                margin: 0,
                fontSize: '36px',
              },
            }}
          >
            {isMuted ? (
              <VolumeOff sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            ) : (
              <VolumeUp sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            )}
          </IconButton>

          {/* Time Display - YouTube format: current / total */}
          <Typography
            sx={{
              color: '#fff',
              fontSize: '16px',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center',
              lineHeight: 1,
              marginLeft: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>

          {/* Spacer to push fullscreen to the right */}
          <Box sx={{ flex: 1 }} />

          {/* Fullscreen Button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }}
            sx={{
              color: '#fff',
              padding: 0,
              width: '54px',
              height: '54px',
              minWidth: '54px',
              minHeight: '54px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiSvgIcon-root': {
                margin: 0,
                fontSize: '36px',
              },
            }}
          >
            {isFullscreen ? (
              <FullscreenExit sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            ) : (
              <Fullscreen sx={{ fontSize: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const CanvasWorkSpace: React.FC<CanvasWorkspaceProps> = ({
  uploadedImage,
  isGenerating,
  isOutpaintActive,
  hasOutpaintAspect,
  isOutpaintFrameLocked,
  canvasDimensions,
  imageBox,
  editImagePrompt,
  isDownloading,
  locationState,
  getRootProps,
  getInputProps,
  handleMouseDown,
  setEditImagePrompt,
  replaceUsingPrompt,
  download,
  handleDeleteImage,
  handleRedo,
  handleUndo,
  canvaImageBoolean,
  generatedImages = [],
  onImageSelect,
  currentImageId,
  viewMode = 'list',
  contentTypeTab = 'image'
}) => {
  const theme = useTheme();
  const location = useLocation();
  const { canvaSettings, setCanvaSettings } = useCanvaSettings();
  const { sections } = useImageModels();
  const promptBoxRef = useRef<HTMLDivElement>(null);

  // Default text-to-image model ID from API (first text-to-image-lora model, e.g. Flux.1 Dev with LoRA)
  const defaultGenerateModelId = useMemo(() => {
    for (const section of sections) {
      const loraModel = section.models?.find(
        m => m.apiModel?.modelType === 'text-to-image-lora'
      );
      if (loraModel?.id) return loraModel.id;
    }
    return null;
  }, [sections]);
  
  // Session-based prompt history - load from sessionStorage on mount
  const STORAGE_KEY = 'promptHistory';
  const loadPromptHistory = (): string[] => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };
  
  const [promptHistory, setPromptHistory] = useState<string[]>(() => loadPromptHistory());
  const [historyIndex, setHistoryIndex] = useState<number>(-1); // -1 means not navigating history
  
  // Persist prompt history to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(promptHistory));
    } catch (error) {
      console.error('Failed to save prompt history to sessionStorage:', error);
    }
  }, [promptHistory]);
  
  // Get model data to access modelType (as fallback if modelType not in canvaSettings)
  const { modelData } = useModelParameters(
    canvaSettings.modelId,
    canvaSettings.modelName || canvaSettings.model
  );
  
  const { showSnackbar } = useSnackbar();
  
  // Determine initial mode - use stored modelType first (instant), then fallback to modelData
  const getInitialMode = (): 'Generate' | 'Edit' => {
    // Priority 0: If navigating from Edit icon, always use Edit mode
    if (locationState?.fromEditIcon) {
      return 'Edit';
    }
    // Priority 1: Use stored modelType from canvaSettings (instant, no API call needed)
    if (canvaSettings.modelType) {
      return getModelCategoryFromType(canvaSettings.modelType);
    }
    // Priority 2: Use modelType from modelData (from useModelParameters hook)
    if (modelData?.modelType) {
      return getModelCategoryFromType(modelData.modelType);
    }
    // Priority 3: Fallback to legacy model name check for old models
    return isEditModel(canvaSettings.model) ? 'Edit' : 'Generate';
  };
  
  const [mode, setMode] = useState<'Generate' | 'Edit'>(getInitialMode());
  
  // Track last edit target we showed snackbar for (show once per image when clicking edit)
  const lastEditIdShownRef = useRef<string | number | null>(null);

  // When navigating from Edit icon on a generated media card: always set dropdown to Edit and switch to edit model
  useEffect(() => {
    if (!locationState?.fromEditIcon) return;
    // Always set mode and model so the dropdown shows "Edit" every time user clicks edit on a card
    setCanvaSettings(settings => ({
      ...settings,
      model: EDIT_MODEL_DISPLAY_NAME,
      modelName: EDIT_MODEL_DISPLAY_NAME,
      modelType: EDIT_MODEL_TYPE,
      modelId: EDIT_MODEL_ID
    }));
    setMode('Edit');
    // Show notification once per edit target (id or timestamp)
    const editKey = locationState?.id ?? locationState?.timestamp ?? null;
    if (editKey != null && lastEditIdShownRef.current !== editKey) {
      lastEditIdShownRef.current = editKey;
      showSnackbar('Switched to edit mode, use prompt in the prompt box to edit', 'info');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.fromEditIcon, locationState?.id, locationState?.timestamp]);

  // Reset snackbar ref when no longer coming from edit icon (so next edit click can show again)
  useEffect(() => {
    if (!locationState?.fromEditIcon) {
      lastEditIdShownRef.current = null;
    }
  }, [locationState?.fromEditIcon]);

  // When navigating from "Use Style" or "Use Persona": set mode to Generate (text-to-image LoRA) and ensure default model for style
  const hasStyleOrPersona = !!(locationState?.style || locationState?.influencer);
  useEffect(() => {
    if (!hasStyleOrPersona || locationState?.fromEditIcon) return;
    setMode('Generate');
    // For art style: set default text-to-image LoRA model (Flux.1 Dev with LoRA). For persona, CanvaFormSection will set project and then the matching LoRA model.
    if (locationState?.style) {
      const modelId = defaultGenerateModelId ?? null;
      setCanvaSettings(settings => ({
        ...settings,
        model: 'Flux.1 Dev with LoRA',
        modelName: 'Flux.1 Dev with LoRA',
        modelType: 'text-to-image-lora',
        modelId
      }));
      if (location.pathname === '/fairgen-ai-studio') {
        try {
          localStorage.setItem('fairgen-ai-studio:selectedModel', JSON.stringify({
            id: modelId,
            modelName: 'Flux.1 Dev with LoRA',
            modelType: 'text-to-image-lora'
          }));
        } catch (e) {
          console.error('Error saving model to localStorage:', e);
        }
      }
    } else {
      // Persona: ensure model type is text-to-image-lora so dropdown shows Generate; CanvaFormSection will set project and LoRA model
      setCanvaSettings(settings => ({
        ...settings,
        modelType: 'text-to-image-lora'
      }));
    }
  }, [hasStyleOrPersona, locationState?.fromEditIcon, locationState?.style, locationState?.influencer, defaultGenerateModelId, location.pathname, setCanvaSettings]);

  // Sync modelType to canvaSettings when available from modelData (for programmatic model changes)
  useEffect(() => {
    if (modelData?.modelType && !canvaSettings.modelType && canvaSettings.modelId) {
      // Update canvaSettings with modelType if it's not already set
      setCanvaSettings(settings => ({
        ...settings,
        modelType: modelData.modelType
      }));
    }
  }, [modelData?.modelType, canvaSettings.modelType, canvaSettings.modelId, setCanvaSettings]);

  // Sync mode with model changes - automatically update when modelType changes
  // This now responds instantly to canvaSettings.modelType changes
  useEffect(() => {
    // Don't override mode when user just clicked Edit on a card (dropdown must stay on Edit)
    if (locationState?.fromEditIcon) {
      return;
    }
    
    // Priority 1: Use stored modelType from canvaSettings (instant, no API call needed)
    let newMode: 'Generate' | 'Edit' = 'Generate';
    if (canvaSettings.modelType) {
      newMode = getModelCategoryFromType(canvaSettings.modelType);
    } 
    // Priority 2: Use modelType from modelData (from useModelParameters hook)
    else if (modelData?.modelType) {
      newMode = getModelCategoryFromType(modelData.modelType);
    }
    // Priority 3: Fallback to legacy model name check for old models
    else {
      newMode = isEditModel(canvaSettings.model) ? 'Edit' : 'Generate';
    }
    setMode(newMode);
  }, [canvaSettings.model, canvaSettings.modelId, canvaSettings.modelName, canvaSettings.modelType, modelData?.modelType, locationState?.fromEditIcon]);


  // Ensure paint area height stays fixed even when image loads
  useEffect(() => {
    const canvasArea = document.querySelector('.canvas-main-area') as HTMLElement;
    if (canvasArea) {
      canvasArea.style.setProperty('height', '500px', 'important');
      canvasArea.style.setProperty('min-height', '500px', 'important');
      canvasArea.style.setProperty('max-height', '500px', 'important');
      canvasArea.style.setProperty('overflow', 'hidden', 'important');
      canvasArea.style.setProperty('flex-shrink', '0', 'important');
      canvasArea.style.setProperty('flex-grow', '0', 'important');
    }
    
    // Also ensure parent container is constrained
    const parentContainer = canvasArea?.parentElement;
    if (parentContainer) {
      parentContainer.style.setProperty('height', '500px', 'important');
      parentContainer.style.setProperty('min-height', '500px', 'important');
      parentContainer.style.setProperty('max-height', '500px', 'important');
      parentContainer.style.setProperty('flex-shrink', '0', 'important');
      parentContainer.style.setProperty('flex-grow', '0', 'important');
    }
    
    // Ensure all images inside are constrained
    const images = canvasArea?.querySelectorAll('img');
    images?.forEach(img => {
      (img as HTMLElement).style.setProperty('max-height', '500px', 'important');
      (img as HTMLElement).style.setProperty('object-fit', 'contain', 'important');
    });
  }, [uploadedImage, isGenerating, canvasDimensions]);

  // Display names for default models (must match what the UI and API use)
  const GENERATE_MODEL_DISPLAY_NAME = 'Flux.1 Dev with LoRA';
  const GENERATE_MODEL_TYPE = 'text-to-image-lora';

  // Handle mode change: set model to default text-to-image (Generate) or default image-editing (Edit)
  const handleModeChange = (newMode: 'Generate' | 'Edit') => {
    const isFairgenStudio = location.pathname === '/fairgen-ai-studio';
    const FAIRGEN_STORAGE_KEY = 'fairgen-ai-studio:selectedModel';

    setMode(newMode);
    if (newMode === 'Generate') {
      const modelId = defaultGenerateModelId ?? null;
      setCanvaSettings(settings => ({
        ...settings,
        model: GENERATE_MODEL_DISPLAY_NAME,
        modelName: GENERATE_MODEL_DISPLAY_NAME,
        modelType: GENERATE_MODEL_TYPE,
        modelId
      }));
      if (isFairgenStudio) {
        try {
          localStorage.setItem(FAIRGEN_STORAGE_KEY, JSON.stringify({
            id: modelId,
            modelName: GENERATE_MODEL_DISPLAY_NAME,
            modelType: GENERATE_MODEL_TYPE
          }));
        } catch (e) {
          console.error('Error saving model to localStorage:', e);
        }
      }
    } else {
      setCanvaSettings(settings => ({
        ...settings,
        model: EDIT_MODEL_DISPLAY_NAME,
        modelName: EDIT_MODEL_DISPLAY_NAME,
        modelType: EDIT_MODEL_TYPE,
        modelId: EDIT_MODEL_ID
      }));
      if (isFairgenStudio) {
        try {
          localStorage.setItem(FAIRGEN_STORAGE_KEY, JSON.stringify({
            id: EDIT_MODEL_ID,
            modelName: EDIT_MODEL_DISPLAY_NAME,
            modelType: EDIT_MODEL_TYPE
          }));
        } catch (e) {
          console.error('Error saving model to localStorage:', e);
        }
      }
      // When switching to Edit mode, use the most recent generated image if available
      if (generatedImages.length > 0 && onImageSelect) {
        const mostRecentResponse = generatedImages[0];
        const mostRecentImage = mostRecentResponse.images[0];
        if (mostRecentImage?.url && mostRecentImage?.id) {
          onImageSelect(mostRecentImage.url, mostRecentImage.id);
        }
      }
    }
  };

  // Track prompts in session history when replaceUsingPrompt is called
  const handleReplaceUsingPrompt = () => {
    const trimmedPrompt = editImagePrompt.trim();
    if (trimmedPrompt) {
      // Add to history if it's different from the last entry (avoid duplicates)
      setPromptHistory(prev => {
        const lastPrompt = prev[prev.length - 1];
        if (lastPrompt !== trimmedPrompt) {
          // Add new prompt to history
          const newHistory = [...prev, trimmedPrompt];
          // Limit history to last 50 prompts to prevent memory issues
          return newHistory.slice(-50);
        }
        return prev;
      });
      // Reset history navigation state
      setHistoryIndex(-1);
    }
    replaceUsingPrompt();
  };

  const handleResetPrompt = () => {
    setEditImagePrompt('');
    setHistoryIndex(-1);
  };

  // Cycle backward through prompt history on each click
  const handleReusePrompt = () => {
    if (promptHistory.length === 0) return;
    
    setHistoryIndex(prevIndex => {
      let newIndex: number;
      
      if (prevIndex === -1) {
        // First click: start from the most recent prompt (last in array)
        newIndex = promptHistory.length - 1;
      } else if (prevIndex > 0) {
        // Subsequent clicks: move backward (toward older prompts)
        newIndex = prevIndex - 1;
      } else {
        // Already at the oldest prompt (index 0), wrap to the newest
        newIndex = promptHistory.length - 1;
      }
      
      // Load the prompt at the new index
      setEditImagePrompt(promptHistory[newIndex]);
      return newIndex;
    });
  };

  // Filter generated content based on contentTypeTab (image vs video)
  const filteredGeneratedContent = useMemo(() => {
    if (!generatedImages || generatedImages.length === 0) return [];
    
    return generatedImages.filter(response => {
      if (!response?.images || response.images.length === 0) return false;
      
      // Filter based on contentTypeTab: only show videos in video mode, only show images in image mode
      if (contentTypeTab === 'video') {
        // In video mode, only show items that are videos
        return response.images.some(img => isVideoUrl(img.url));
      } else {
        // In image mode, only show items that are NOT videos
        return response.images.some(img => !isVideoUrl(img.url));
      }
    });
  }, [generatedImages, contentTypeTab]);

  // Calculate total number of items in the filtered content
  const totalGeneratedContent = filteredGeneratedContent?.reduce((total, response) => total + (response?.images?.length || 0), 0) || 0;

  // Calculate grid columns dynamically based on image count (Grid View only)
  const getGridColumns = (count: number): string => {
    if (count === 1) return '1fr';
    if (count === 2) return 'repeat(2, 1fr)';
    if (count === 3 || count === 4) return 'repeat(2, 1fr)';
    // 5+ images use 3 columns
    return 'repeat(3, 1fr)';
  };

  // Get current content URL - only from filtered content for the active mode
  // Also check if uploadedImage matches the current mode (don't show video in image mode or vice versa)
  const uploadedImageMatchesMode = uploadedImage ? 
    (contentTypeTab === 'video' ? isVideoUrl(uploadedImage) : !isVideoUrl(uploadedImage)) : 
    false;
  
  const currentImageUrl = (uploadedImageMatchesMode ? uploadedImage : null) || 
    (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0 ? filteredGeneratedContent[0]?.images?.[0]?.url : null);
  
  // Determine if current content is a video - only check if we're in video mode and the URL is actually a video
  const isVideo = contentTypeTab === 'video' && currentImageUrl ? isVideoUrl(currentImageUrl) : false;

  // Get current displayed image for CanvasActionBar
  const getCurrentImageForActionBar = (): SaagasImage | null => {
    if (!canvaImageBoolean || !filteredGeneratedContent || filteredGeneratedContent.length === 0) {
      return null;
    }

    // Try to find the currently selected image by currentImageId
    if (currentImageId) {
      for (const response of filteredGeneratedContent) {
        const foundImage = response.images.find(img => img.id === currentImageId);
        if (foundImage) {
          return {
            id: foundImage.id,
            url: foundImage.url,
            artStyleId: foundImage.artStyleId,
            imageCategories: foundImage.imageCategories,
            projectId: foundImage.projectId,
            prompt: editImagePrompt || '',
            liked: false, // Default to false, will be updated by the component
          };
        }
      }
    }

    // Fallback to first image if no currentImageId or not found
    const firstImage = filteredGeneratedContent[0]?.images?.[0];
    if (firstImage) {
      return {
        id: firstImage.id,
        url: firstImage.url,
        artStyleId: firstImage.artStyleId,
        imageCategories: firstImage.imageCategories,
        projectId: firstImage.projectId,
        prompt: editImagePrompt || '',
        liked: false,
      };
    }

    return null;
  };

  const currentImageForActionBar = getCurrentImageForActionBar();

  // Debug: Log filteredGeneratedContent to verify it's being passed correctly
  useEffect(() => {
    if (canvaImageBoolean) {
      console.log('CanvasWorkSpace - filteredGeneratedContent:', filteredGeneratedContent);
      console.log('CanvasWorkSpace - filteredGeneratedContent.length:', filteredGeneratedContent?.length);
      console.log('CanvasWorkSpace - totalGeneratedContent:', totalGeneratedContent);
      console.log('CanvasWorkSpace - contentTypeTab:', contentTypeTab);
      console.log('CanvasWorkSpace - canvaImageBoolean:', canvaImageBoolean);
      console.log('CanvasWorkSpace - uploadedImage:', uploadedImage);
      console.log('CanvasWorkSpace - uploadedImageMatchesMode:', uploadedImageMatchesMode);
      console.log('CanvasWorkSpace - Condition check:', {
        canvaImageBoolean,
        hasFilteredContent: !!filteredGeneratedContent && filteredGeneratedContent.length > 0,
        totalGeneratedContent,
        totalGreaterThanOne: totalGeneratedContent > 1,
        notUploadedImageMode: !uploadedImageMatchesMode,
        shouldShowStacked: canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 1 && !uploadedImageMatchesMode
      });
    }
  }, [filteredGeneratedContent, canvaImageBoolean, contentTypeTab, totalGeneratedContent, uploadedImage, uploadedImageMatchesMode]);

  return (
    <Box sx={{ display: 'flex' }}>
      

      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%', 
        pl: 2, 
        pt: 1.5,
        gap: canvaImageBoolean ? 2 : 0,
        ml: canvaImageBoolean ? 0 : 9, 
        alignItems: 'flex-start',
        minWidth: 0
      }}>
        {/* Only show stacked view when multiple images exist AND not currently editing (uploadedImage means editing) */}
        {(() => {
          const shouldShowStacked = canvaImageBoolean && 
            filteredGeneratedContent && 
            filteredGeneratedContent.length > 0 && 
            totalGeneratedContent > 1 && 
            !uploadedImage;
          
          // Log condition check for debugging
          if (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0) {
            console.log('🔍 Stacked view condition check (render time):', {
              canvaImageBoolean,
              hasFilteredContent: !!filteredGeneratedContent && filteredGeneratedContent.length > 0,
              totalGeneratedContent,
              totalGreaterThanOne: totalGeneratedContent > 1,
              uploadedImage,
              notUploadedImage: !uploadedImage,
              shouldShowStacked,
              viewMode
            });
          }
          
          return shouldShowStacked;
        })() ? (
          // Display all generated images in grid or list view (multiple images stacked)
            <Box
              sx={{
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                height: viewMode === 'grid' ? 'auto' : '626px',
                minHeight: viewMode === 'grid' ? 'auto' : '400px',
                maxHeight: viewMode === 'grid' ? '626px' : '626px',
                display: viewMode === 'grid' ? 'grid' : 'flex',
                gridTemplateColumns: viewMode === 'grid' ? getGridColumns(totalGeneratedContent) : 'none',
                gridAutoRows: viewMode === 'grid' ? 'min-content' : 'none',
                gridAutoFlow: viewMode === 'grid' ? 'row' : 'unset',
                flexDirection: viewMode === 'list' ? 'column' : 'unset',
                gap: viewMode === 'grid' ? 3 : 2,
                rowGap: viewMode === 'grid' ? 3 : undefined,
                columnGap: viewMode === 'grid' ? 3 : undefined,
                alignItems: viewMode === 'list' ? 'center' : 'start',
                alignContent: viewMode === 'grid' ? 'start' : 'unset',
                justifyItems: viewMode === 'grid' ? 'stretch' : 'unset',
                alignSelf: viewMode === 'grid' ? 'start' : 'unset',
                placeItems: viewMode === 'grid' ? 'start' : 'unset',
                overflowY: viewMode === 'grid' ? 'auto' : 'auto',
                overflowX: 'hidden',
                scrollBehavior: 'smooth',
                paddingTop: viewMode === 'grid' ? '16px !important' : '16px',
                paddingBottom: viewMode === 'grid' ? '16px !important' : '16px',
                paddingLeft: '16px',
                paddingRight: '16px',
                border: `1px solid ${grey[800]}`,
                borderRadius: 2,
                boxSizing: 'border-box',
                flexShrink: 0,
                flexGrow: 0,
                '& > .img-row:not(:first-of-type) img': {
                  opacity: viewMode === 'list' ? 0.4 : 1
                }
              }}
            >
              {isGenerating && viewMode === 'grid' && totalGeneratedContent === 0 ? (
              // Grid mode: show loading indicators in 2-column grid matching the grid layout
                Array(canvaSettings.number_of_images || 1)
                  .fill(0)
                  .map((_, index) => (
                    <Box
                      key={`loading-${index}`}
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        minHeight: '200px',
                        backgroundColor: '#181818',
                        borderRadius: 2,
                        marginTop: index < 2 ? '16px' : '0',
                        position: 'relative',
                        '& > div': {
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }
                      }}
                    >
                      <LoadingCanva 
                        numImages={1} 
                        imageDimensions={canvaSettings.image_dimensions} 
                        mobileGrid={'1'} 
                        sx={{ width: '100%', height: '100%' }}
                      />
                    </Box>
                  ))
              ) : isGenerating && totalGeneratedContent === 0 ? (
                <LoadingCanva 
                  numImages={canvaSettings.number_of_images} 
                  imageDimensions={canvaSettings.image_dimensions} 
                  mobileGrid={'1'} 
                  sx={{ width: '450px', height: '450px' }}
                />
              ) : null}
              {viewMode === 'grid' ? (
              // Grid view: flatten all images into a single grid (show all images)
                filteredGeneratedContent.flatMap((response) => {
                  if (!response || !response.images || response.images.length === 0) {
                    return [];
                  }
                  return response.images;
                }).map((image) => (
                  <Box
                    key={image.id}
                    onClick={(e) => {
                    // Don't trigger image select if clicking on action bar
                      if ((e.target as HTMLElement).closest('.canvas-action-bar')) {
                        return;
                      }
                      if (onImageSelect) {
                        onImageSelect(image.url, image.id);
                      }
                    }}
                    onMouseEnter={(e) => {
                    // Don't trigger hover effects if hovering over action bar
                      if ((e.target as HTMLElement).closest('.canvas-action-bar')) {
                        e.stopPropagation();
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: currentImageId === image.id ? '2px solid #3b82f6' : '2px solid transparent',
                      borderRadius: 2,
                      padding: 0,
                      margin: 0,
                      transition: 'all 0.2s',
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      minHeight: 0,
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      backgroundColor: '#181818',
                      position: 'relative',
                      boxSizing: 'border-box',
                      alignSelf: 'start',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        '& .canvas-action-bar': {
                          opacity: 1,
                          visibility: 'visible',
                        }
                      },
                      '& .canvas-action-bar': {
                        opacity: 1,
                        visibility: 'visible',
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: 'auto',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        minHeight: 0,
                        minWidth: 0,
                      }}
                    >
                      {/* CanvasActionBar for each image in grid view */}
                      {canvaImageBoolean && (
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            // top: 5, 
                            zIndex: 1000, 
                            pointerEvents: 'auto',
                            overflow: 'visible',
                            boxSizing: 'border-box',
                            width: '100%',
                          }}
                          onMouseEnter={(e) => e.stopPropagation()}
                          onMouseLeave={(e) => e.stopPropagation()}
                        >
                          <CanvasActionBar
                            image={{
                              id: image.id,
                              url: image.url,
                              artStyleId: image?.artStyleId,
                              imageCategories: image?.imageCategories,
                              projectId: image?.projectId,
                              prompt: editImagePrompt || '',
                              liked: false,
                            }}
                            viewMode={viewMode}
                            onEdit={() => {
                              if (onImageSelect) {
                                onImageSelect(image.url, image.id);
                              }
                            }}
                          />
                        </Box>
                      )}
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          width: '100%', 
                          height: 'auto', 
                          overflow: 'hidden', 
                          zIndex: 1,
                          pointerEvents: 'auto',
                          opacity: 1,
                          visibility: 'visible',
                          display: 'block',
                          margin: 0,
                          padding: 0,
                          minHeight: 0,
                          minWidth: 0,
                        }}
                        onMouseEnter={(e) => {
                        // Keep image visible when hovering
                          e.stopPropagation();
                        }}
                        onMouseLeave={(e) => {
                        // Keep image visible when leaving
                          e.stopPropagation();
                        }}
                      >
                        <Box
                          component="img"
                          src={image.url}
                          alt="Generated"
                          onLoad={(e) => {
                            // Ensure image maintains its natural aspect ratio
                            const img = e.currentTarget as HTMLImageElement;
                            img.style.width = '100%';
                            img.style.height = 'auto';
                            img.style.maxWidth = '100%';
                            img.style.objectFit = 'contain';
                          }}
                          sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 'none',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            margin: 0,
                            padding: 0,
                            verticalAlign: 'top',
                            aspectRatio: 'auto',
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
              // List view: flatten all images and display vertically (one per row)
                filteredGeneratedContent.flatMap((response) => {
                  if (!response || !response.images || response.images.length === 0) {
                    return [];
                  }
                  return response.images;
                }).map((image) => (
                  <Box
                    key={image.id}
                    className="img-row"
                    onClick={(e) => {
                    // Don't trigger image select if clicking on action bar
                      if ((e.target as HTMLElement).closest('.canvas-action-bar')) {
                        return;
                      }
                      if (onImageSelect) {
                        onImageSelect(image.url, image.id);
                      }
                    }}
                    onMouseEnter={(e) => {
                    // Don't trigger hover effects if hovering over action bar
                      if ((e.target as HTMLElement).closest('.canvas-action-bar')) {
                        e.stopPropagation();
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: currentImageId === image.id ? '2px solid #3b82f6' : '2px solid transparent',
                      borderRadius: 2,
                      padding: currentImageId === image.id ? '2px' : '0',
                      transition: 'all 0.2s',
                      position: 'relative',
                      overflow: 'hidden',
                      width: '100%',
                      height: '500px',
                      minHeight: '500px',
                      maxHeight: '500px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0,
                      backgroundColor: '#18181B',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        padding: '2px',
                        '& .canvas-action-bar': {
                          opacity: 1,
                          visibility: 'visible',
                        }
                      },
                      '& .canvas-action-bar': {
                        opacity: 1,
                        visibility: 'visible',
                      }
                    }}
                  >
                    {/* CanvasActionBar for each image in list view */}
                    {canvaImageBoolean && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          right: 0,
                          width: '100%',
                          maxWidth: '100%',
                          zIndex: 1000, 
                          pointerEvents: 'auto',
                          overflow: 'visible',
                          boxSizing: 'border-box',
                        }}
                        onMouseEnter={(e) => e.stopPropagation()}
                        onMouseLeave={(e) => e.stopPropagation()}
                      >
                        <CanvasActionBar
                          image={{
                            id: image.id,
                            url: image.url,
                            artStyleId: image?.artStyleId,
                            imageCategories: image?.imageCategories,
                            projectId: image?.projectId,
                            prompt: editImagePrompt || '',
                            liked: false,
                          }}
                          onEdit={() => {
                            if (onImageSelect) {
                              onImageSelect(image.url, image.id);
                            }
                          }}
                        />
                      </Box>
                    )}
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        width: '100%', 
                        height: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        zIndex: 1,
                        pointerEvents: 'auto',
                        opacity: 1,
                        visibility: 'visible',
                      }}
                      onMouseEnter={(e) => {
                      // Keep image visible when hovering
                        e.stopPropagation();
                      }}
                      onMouseLeave={(e) => {
                      // Keep image visible when leaving
                        e.stopPropagation();
                      }}
                    >
                      <Box
                        component="img"
                        src={image.url}
                        alt="Generated"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          ) : (
          // Single image paint area - shows one image at a time (for single image generation or editing)
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: '500px', 
              minHeight: '500px', 
              maxHeight: '500px', 
              flexShrink: 0,
              flexGrow: 0,
              alignSelf: 'flex-start'
            }}>
              <Box
                {...(!uploadedImage && !isGenerating && !canvaImageBoolean ? getRootProps() : {})}
                className="canvas-main-area"
                sx={{
                  height: '500px !important',
                  minHeight: '500px !important',
                  maxHeight: '500px !important',
                  width: '100%',
                  position: 'relative',
                  background: isGenerating || uploadedImageMatchesMode || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0) ? 'transparent' : '#18181B',
                  borderRadius: 2,
                  border: `1px solid ${grey[800]}`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: isGenerating ? 'not-allowed' : uploadedImageMatchesMode || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0) ? 'default' : (!canvaImageBoolean ? 'pointer' : 'default'),
                  pointerEvents: isGenerating ? 'none' : 'auto',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  flexGrow: 0,
                  '& *': {
                    maxHeight: '100%'
                  },
                  '&:hover .hover-icon': {
                    opacity: (uploadedImageMatchesMode || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0)) && !isGenerating ? 1 : 0
                  }
                }}
              >
                <>
                  {!isGenerating && !canvaImageBoolean && <input {...getInputProps()} />}
                  {!uploadedImageMatchesMode && !isGenerating && !(canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0) && (
                    <>
                      {canvaImageBoolean ? (
                      // For fairgen-ai-studio: Show center text only
                        <Typography color={theme.palette.text.disabled} fontWeight={400} fontSize={'16px'} fontFamily={'Inter'} textAlign="center">
                      Type a prompt below to generate {contentTypeTab === 'video' ? 'a video' : 'an image'} …
                        </Typography>
                      ) : (
                      // For canvas-ai-editor: Keep original UI
                        <Box textAlign="center" alignItems={'center'} display="flex" flexDirection="column" gap={1}>
                          <img src={CloudUploadIcon} alt="upload" width={55} />
                          <Typography color={theme.palette.text.disabled} mt={1} fontWeight={400} fontSize={'16px'} fontFamily={'Inter'}>
                        Click to upload an image to edit
                          </Typography>
                          <Button
                            variant="outlined"
                            component="span"
                            size="small"
                            sx={{
                              mt: 1,
                              width: 98,
                              height: 28,
                              fontSize: 12,
                              borderRadius: 170,
                              color: theme.palette.text.disabled,
                              borderColor: grey[700],
                              textTransform: 'none',
                              '&:hover': {
                                borderColor: grey[500],
                                backgroundColor: 'transparent'
                              }
                            }}
                          >
                        Choose File
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>

                {isGenerating && (
                  <LoadingCanva
                    numImages={1}
                    imageDimensions={`${locationState?.width}x${locationState?.height}`}
                    mobileGrid={'1'}
                    sx={{ width: '450px', height: '450px' }}
                  />
                )}

                {(uploadedImageMatchesMode || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0)) && !isGenerating && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems={'center'}
                    justifyContent={'center'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      maxHeight: '500px',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      flexShrink: 0,
                      flexGrow: 0,
                      '&:hover .hover-icon': {
                        opacity: 1
                      }
                    }}
                  >
                    {/* ActionBar - Use VideoTopActionBar for videos, CanvasActionBar for images */}
                    {canvaImageBoolean && currentImageForActionBar && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          maxWidth: '100%',
                          zIndex: 1000,
                          pointerEvents: 'none', // Allow pointer events to pass through to resize handles
                          overflow: 'visible',
                        }}
                      >
                        <Box sx={{ pointerEvents: 'auto' }}> {/* Re-enable pointer events for the action bar buttons */}
                          {isVideo ? (
                            <VideoTopActionBar 
                              video={currentImageForActionBar} 
                              viewMode={viewMode}
                            />
                          ) : (
                            <CanvasActionBar 
                              image={currentImageForActionBar} 
                              viewMode={viewMode} 
                              c2pa={true}
                              onEdit={() => {
                                if (onImageSelect && currentImageForActionBar) {
                                  onImageSelect(currentImageForActionBar.url, currentImageForActionBar.id);
                                }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                    {isOutpaintActive && hasOutpaintAspect && !isOutpaintFrameLocked ? (
                      <Box
                        className="checkerboard-bg"
                        sx={{
                          width: `${Math.min(canvasDimensions.width, typeof window !== 'undefined' ? window.innerWidth * 0.95 : 800)}px`,
                          height: `${Math.min(canvasDimensions.height, 500)}px !important`,
                          maxHeight: '500px !important',
                          maxWidth: '100%',
                          position: 'relative',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        <Box
                          onMouseDown={e => handleMouseDown(e, 'drag')}
                          sx={{
                            position: 'absolute',
                            width: imageBox.width,
                            height: imageBox.height,
                            top: imageBox.y,
                            left: imageBox.x,
                            cursor: 'move',
                            border: '2px solid #3b82f6',
                            boxSizing: 'border-box'
                          }}
                        >
                          <Box
                            component="img"
                            src={uploadedImage}
                            alt="Uploaded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              pointerEvents: 'none'
                            }}
                          />

                          <Box className="resize-handle tl" onMouseDown={e => handleMouseDown(e, 'resize', 'tl')} />
                          <Box className="resize-handle tr" onMouseDown={e => handleMouseDown(e, 'resize', 'tr')} />
                          <Box className="resize-handle bl" onMouseDown={e => handleMouseDown(e, 'resize', 'bl')} />
                          <Box className="resize-handle br" onMouseDown={e => handleMouseDown(e, 'resize', 'br')} />
                        </Box>
                      </Box>
                    ) : isVideo && currentImageUrl ? (
                    // Video player - show video with custom play/pause controls
                      <VideoPlayer 
                        src={currentImageUrl}
                        sx={{
                          position: 'relative',
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '500px',
                          borderRadius: 2,
                          border: `1px solid ${grey[800]}`,
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={uploadedImage || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0 ? filteredGeneratedContent[0]?.images?.[0]?.url : null)}
                        alt="Uploaded"
                        onLoad={(e) => {
                        // Ensure image maintains its natural aspect ratio
                          const img = e.currentTarget as HTMLImageElement;
                          const naturalWidth = img.naturalWidth;
                          const naturalHeight = img.naturalHeight;
                          if (naturalWidth && naturalHeight) {
                            const aspectRatio = naturalWidth / naturalHeight;
                            const containerWidth = img.parentElement?.clientWidth || 0;
                            const maxHeight = 500;
                          
                            // Calculate display dimensions maintaining aspect ratio
                            let displayWidth: number;
                            let displayHeight: number;
                          
                            if (containerWidth / maxHeight > aspectRatio) {
                            // Container is wider than image aspect ratio - fit to height
                              displayHeight = maxHeight;
                              displayWidth = displayHeight * aspectRatio;
                            } else {
                            // Container is taller than image aspect ratio - fit to width
                              displayWidth = containerWidth;
                              displayHeight = displayWidth / aspectRatio;
                            }
                          
                            // Apply calculated dimensions to maintain aspect ratio
                            img.style.width = `${displayWidth}px`;
                            img.style.height = `${displayHeight}px`;
                          }
                        }}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '500px',
                          objectFit: 'contain',
                          borderRadius: 2,
                          border: (uploadedImage || (canvaImageBoolean && filteredGeneratedContent && filteredGeneratedContent.length > 0 && totalGeneratedContent > 0)) ? `1px solid ${grey[800]}` : '',
                          display: 'block',
                          height: 'auto',
                          width: 'auto'
                        }}
                      />
                    )}

                    {/* Download icon - Only show when NOT canvaImageBoolean (fairgen uses CanvasActionBar) */}
                    {!canvaImageBoolean && (
                      <Box
                        component="img"
                        src={Downloadicon}
                        alt="Download"
                        onClick={!isDownloading ? download : undefined}
                        className="hover-icon"
                        sx={{
                          position: 'absolute',
                          bottom: '2%',
                          right: '53px',
                          width: 36,
                          height: 38,
                          opacity: 0,
                          transition: 'opacity 0.3s',
                          cursor: isDownloading ? 'wait' : 'pointer',
                          filter: isDownloading ? 'grayscale(1)' : 'none'
                        }}
                      />
                    )}
                    {/* Delete icon - Only show when NOT canvaImageBoolean (fairgen uses CanvasActionBar) */}
                    {!canvaImageBoolean && (
                      <Box
                        component="img"
                        src={Deleteicon}
                        alt="Delete"
                        onClick={handleDeleteImage}
                        className="hover-icon"
                        sx={{
                          position: 'absolute',
                          bottom: '2%',
                          right: '5px',
                          width: 36,
                          height: 38,
                          opacity: 0,
                          transition: 'opacity 0.3s',
                          cursor: 'pointer'
                        }}
                      />
                    )}
                  </Box>
                )}

                {/* Upload icon - Always show when canvaImageBoolean so user can upload another image */}
                {canvaImageBoolean && !isGenerating && (
                  <Tooltip title="Click to upload an image to edit" placement="top">
                    <Box
                      {...getRootProps()}
                      component="div"
                      sx={{
                        position: 'absolute',
                        bottom: '1%',
                        right: '10px',
                        width: 36,
                        height: 38,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 1,
                        transition: 'opacity 0.3s',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    >
                      <input {...getInputProps()} />
                      {contentTypeTab === 'video' ? <></> : <img src={CloudUploadIcon} alt="upload" width={26} height={26} />}
                    </Box>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}

        <Box 
          mt={2} 
          display="grid" 
          alignItems="center" 
          width="100%" 
        >
          
          {canvaImageBoolean?<>
            <Box 
              ref={promptBoxRef}
              mt={2} 
              display="grid" 
              alignItems="center" 
              width="100%" 
              gridTemplateColumns={contentTypeTab === 'video' ? '100%' : '95.5%'}
              sx={{
                minWidth: 0
              }}
            >
          
              <Box sx={{ width: '100%' }}>
                <TextField
                  id="generate-image"
                  variant="outlined"
                  minRows={4}
                  fullWidth
                  placeholder={uploadedImage ? "Type a prompt to edit..." : contentTypeTab === 'video' ? "Type a prompt to generate a video..." : "Type a prompt to generate an image..."}
                  value={editImagePrompt}
                  onChange={e => {
                    setEditImagePrompt(e.target.value);
                    // Exit history navigation when user types
                    if (historyIndex !== -1) {
                      setHistoryIndex(-1);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleReplaceUsingPrompt();
                    }
                  }}
                  multiline
                  maxRows={2.2}
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-input': {
                      color: 'white',
                      padding: theme.spacing(0, 1),
                      '&::placeholder': { fontWeight: 470 },
                    },
                    '& .MuiOutlinedInput-root': {
                      background: '#18181B',
                      padding: '8px 12px',
                      borderRadius: 3,
                      '& fieldset': { border: `1px solid ${grey[800]}` },
                      '&:hover fieldset': { border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}` },
                      '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}` }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                 
                
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      ><Box></Box> 
                        <Box  sx={{ display: 'flex' ,gap:1}}>

                          <Tooltip title="Clean prompt box" placement="top">
                            <IconButton
                              onClick={handleResetPrompt}
                              size="small"
                              sx={{
                                background:  "#252424",
                                borderRadius: "4px",
                                width: "34px",
                                height: "34px",
                                padding: "1px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Copy the last used prompts to the prompt box" placement="top">
                            <IconButton
                              onClick={handleReusePrompt}
                              size="small"
                              disabled={promptHistory.length === 0}
                              sx={{
                                background: "#252424",
                                borderRadius: 2,
                                width: "34px",
                                height: "34px",
                                padding: "1px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: promptHistory.length === 0 ? 0.5 : 1,
                                cursor: promptHistory.length === 0 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <CycleIcon />
                            </IconButton>
                          </Tooltip>
                          {contentTypeTab !== 'video' && (
                            <Select
                              value={mode}
                              onChange={(e) => handleModeChange(e.target.value as 'Generate' | 'Edit')}
                              variant="outlined"
                              sx={{
                                "& .MuiSelect-select": {
                                  // padding: "8px 12px !important",
                                  paddingTop: "5px !important",
                                  color: "white !important",
                                  fontSize: "14px !important",
                                }
                              }}
                            >
                              <MenuItem value="Generate">Generate</MenuItem>
                              <MenuItem value="Edit">Edit</MenuItem>
                            </Select>
                          )}
                    
                        </Box>
                        <Box sx={{ display: 'flex' ,justifyContent:'flex-end'}}>
                          <IconButton
                            onClick={handleReplaceUsingPrompt}
                            disabled={isGenerating || !editImagePrompt.trim()}
                            sx={{
                              opacity: (isGenerating || !editImagePrompt.trim()) ? 0.5 : 1,
                              cursor: (isGenerating || !editImagePrompt.trim()) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <img src={arrow} alt="arrow" style={{ width: '28px', height: '28px' }} />
                          </IconButton>
                        </Box>
                      </Box>
                  
                    )
                  }}
            
                />
              </Box>
            </Box>
          </>:
            <Box 
              display="grid" 
              alignItems="center" 
              width="100%"
              mb={2} 
            >
          
              <Box sx={{ width: '100%' }}>
                <TextField
                  id="generate-image"
                  variant="outlined"
                  minRows={3}
                  fullWidth
                  placeholder={uploadedImage ? "Type a prompt to edit..." : "Type a prompt to generate an image..."}
                  value={editImagePrompt}
                  onChange={e => setEditImagePrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      replaceUsingPrompt();
                    }
                  }}
                  multiline
                  maxRows={6}
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-input': {
                      color: 'white',
                      padding: theme.spacing(0, 1),
                      '&::placeholder': { fontWeight: 470 },
                    },
                    '& .MuiOutlinedInput-root': {
                      background: '#18181B',
                      padding: '8px 12px',
                      borderRadius: 3,
                      '& fieldset': { border: `1px solid ${grey[800]}` },
                      '&:hover fieldset': { border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}` },
                      '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.mode === 'light' ? grey[400] : grey[700]}` }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={replaceUsingPrompt}
                        disabled={isGenerating || !editImagePrompt.trim()}
                        sx={{ 
                          padding: 0,
                          opacity: (isGenerating || !editImagePrompt.trim()) ? 0.5 : 1,
                          cursor: (isGenerating || !editImagePrompt.trim()) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <img src={arrow} alt="arrow" style={{ width: '34px', height: '34px' }} />
                      </IconButton>
                    )
                  }}
                />
              </Box>
            </Box>
          }
            
            
          
          <Box>
            {contentTypeTab !== 'video' && handleUndo && (
              <UndoRedoControls
                isGenerating={isGenerating}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                flexDirectionType="column"
              />
            )}
          </Box>
         
          
        </Box>


      </Box>
    </Box>
  );
};

export default CanvasWorkSpace;